/**
 * Utility functions for input validation and sanitization
 */

/**
 * Sanitize HTML content to prevent XSS attacks
 */
export const sanitizeHtml = (input: string): string => {
  return input
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;")
    .replace(/\//g, "&#x2F;");
};

/**
 * Validate email format with additional security checks
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 254; // RFC 5321 limit
};

/**
 * Sanitize user input for comments and text fields
 */
export const sanitizeUserInput = (input: string): string => {
  return input
    .trim()
    .replace(/\s+/g, " ") // Replace multiple spaces with single space
    .substring(0, 1000); // Limit length to prevent abuse
};

/**
 * Validate phone number format
 */
export const isValidPhoneNumber = (phone: string): boolean => {
  const phoneRegex = /^\+?[1-9]\d{0,15}$/;
  return phoneRegex.test(phone.replace(/[\s\-()]/g, ""));
};

/**
 * Check if a date is within acceptable booking range
 */
export const isValidBookingDate = (date: Date): boolean => {
  const today = new Date();
  const maxFutureDate = new Date();
  maxFutureDate.setFullYear(today.getFullYear() + 1); // Max 1 year in future

  return date >= today && date <= maxFutureDate;
};

/**
 * Check if a booking date is too soon (less than 2 days in advance).
 * Compares at midnight granularity so timezone edge cases are handled.
 */
export const isDateTooSoon = (date: Date): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() + 2);
  const check = new Date(date);
  check.setHours(0, 0, 0, 0);
  return check < minDate;
};
